// src/utils/sentimentAnalysis.js
// ============================================================
// Tenafi AI — Tenant Response Sentiment Analysis
// Ported from the TypeScript sentiment engine.
//
// Analyzes free-text SMS replies from tenants to classify:
//   sentiment:          positive | neutral | negative | hostile
//   paymentLikelihood:  high | medium | low | very_low
//   escalationRisk:     none | low | moderate | high | critical
//
// This is rule-based (no API required) — fast, free, auditable.
// ============================================================

const POSITIVE_SIGNALS = [
  'will pay', 'paying', 'sending', 'sent', 'transferred', 'paid',
  'yes', 'sure', 'absolutely', 'of course', 'no problem',
  'friday', 'tomorrow', 'today', 'this week', 'by end of',
  'thank you', 'thanks', 'appreciate', 'sorry for the delay',
  'on it', 'working on it', 'getting it together',
  'payment plan', 'can we set up', 'installments',
];

const NEGATIVE_SIGNALS = [
  "can't", 'cannot', 'unable', "don't have", 'no money',
  'lost my job', 'unemployed', 'laid off', 'fired',
  'medical', 'hospital', 'emergency', 'accident',
  'next month', 'few weeks', 'not sure when',
  'struggling', 'difficult', 'hard time', 'tough',
];

const HOSTILE_SIGNALS = [
  'lawyer', 'attorney', 'sue', 'lawsuit', 'legal action',
  'unfair', 'illegal', 'harassment', 'stop contacting',
  'leave me alone', 'do not contact', 'unsubscribe',
  'rights', 'tenant rights', 'discrimination',
  'report', 'complaint', 'housing authority',
  'evict', "you can't evict", 'see you in court',
];

const PROMISE_PATTERNS = [
  /will pay (by |on |this |next )/i,
  /pay(ing)? (on |by |this |next )/i,
  /send(ing)? (it |payment |money )?(today|tomorrow|friday|monday|this week)/i,
  /i('ll| will) (have|get|send|transfer|pay)/i,
  /give me (a |until |till )/i,
  /by (end of |this |next )/i,
];

const DISPUTE_PATTERNS = [
  /not (fair|right|correct|accurate)/i,
  /wrong (amount|balance|charge)/i,
  /already paid/i,
  /double charged/i,
  /maintenance|repair|fix/i,
  /uninhabitable|unsafe|broken/i,
];

function countMatches(text, keywords) {
  const lower = text.toLowerCase();
  return keywords.filter(k => lower.includes(k)).length;
}

function matchesPatterns(text, patterns) {
  return patterns
    .filter(p => p.test(text))
    .map(p => { const m = text.match(p); return m ? m[0] : ''; })
    .filter(Boolean);
}

/**
 * Analyse a single free-text tenant response.
 *
 * @param {string} responseText  - The raw SMS reply
 * @param {object} context       - Optional context: { daysLate, previousPromises, currentBalance, riskScore }
 * @returns {{ sentiment, confidence, paymentLikelihood, escalationRisk, summary, signals, suggestedAction }}
 */
export function analyzeSentiment(responseText, context = {}) {
  if (!responseText || responseText.trim().length === 0) {
    return {
      sentiment: 'neutral',
      confidence: 50,
      paymentLikelihood: 'medium',
      escalationRisk: 'none',
      summary: 'No response text to analyze',
      signals: [],
      suggestedAction: 'Follow up with another message',
    };
  }

  const text = responseText.trim();
  const positiveCount = countMatches(text, POSITIVE_SIGNALS);
  const negativeCount = countMatches(text, NEGATIVE_SIGNALS);
  const hostileCount = countMatches(text, HOSTILE_SIGNALS);
  const promiseMatches = matchesPatterns(text, PROMISE_PATTERNS);
  const disputeMatches = matchesPatterns(text, DISPUTE_PATTERNS);

  const signals = [];
  let sentimentScore = 0; // -100 to +100

  // Positive signals
  sentimentScore += positiveCount * 15;
  if (promiseMatches.length > 0) {
    sentimentScore += 20;
    signals.push(`Payment promise detected: "${promiseMatches[0]}"`);
  }
  if (positiveCount > 0) signals.push(`${positiveCount} positive indicator(s) found`);

  // Negative signals
  sentimentScore -= negativeCount * 20;
  if (negativeCount > 0) signals.push(`${negativeCount} hardship indicator(s) detected`);

  // Hostile signals
  sentimentScore -= hostileCount * 35;
  if (hostileCount > 0) signals.push(`${hostileCount} escalation signal(s) detected`);

  // Disputes
  if (disputeMatches.length > 0) {
    sentimentScore -= 10;
    signals.push(`Dispute language detected: "${disputeMatches[0]}"`);
  }

  // Context adjustments
  if (context.previousPromises >= 2) {
    sentimentScore -= 15;
    signals.push(`${context.previousPromises} previous unfulfilled promises`);
  }
  if (context.daysLate > 30) {
    sentimentScore -= 10;
    signals.push(`Severely overdue (${context.daysLate} days)`);
  }
  if (context.riskScore >= 80) {
    sentimentScore -= 5;
    signals.push('High baseline risk score');
  }

  sentimentScore = Math.max(-100, Math.min(100, sentimentScore));

  // Sentiment label
  let sentiment;
  if (hostileCount >= 2 || sentimentScore <= -60) sentiment = 'hostile';
  else if (sentimentScore <= -20) sentiment = 'negative';
  else if (sentimentScore >= 20) sentiment = 'positive';
  else sentiment = 'neutral';

  // Payment likelihood
  let paymentLikelihood;
  if (sentiment === 'positive' && promiseMatches.length > 0) {
    paymentLikelihood = (context.previousPromises >= 2) ? 'medium' : 'high';
  } else if (sentiment === 'positive') {
    paymentLikelihood = 'medium';
  } else if (sentiment === 'neutral') {
    paymentLikelihood = 'medium';
  } else if (sentiment === 'negative') {
    paymentLikelihood = 'low';
  } else {
    paymentLikelihood = 'very_low';
  }

  // Escalation risk
  let escalationRisk;
  if (hostileCount >= 3 || disputeMatches.length >= 2) escalationRisk = 'critical';
  else if (hostileCount >= 1) escalationRisk = 'high';
  else if (disputeMatches.length >= 1 || (sentiment === 'negative' && (context.daysLate || 0) > 30)) escalationRisk = 'moderate';
  else if (sentiment === 'negative') escalationRisk = 'low';
  else escalationRisk = 'none';

  // Confidence
  const totalSignals = positiveCount + negativeCount + hostileCount + promiseMatches.length + disputeMatches.length;
  const confidence = Math.min(95, 40 + totalSignals * 12);

  // Summary
  let summary;
  if (sentiment === 'hostile') {
    summary = 'Tenant response indicates potential legal escalation. Recommend pausing automated outreach and escalating to management.';
  } else if (sentiment === 'negative') {
    summary = 'Tenant is expressing difficulty or hardship. Consider offering a payment plan.';
  } else if (sentiment === 'positive' && promiseMatches.length > 0) {
    const reliability = (context.previousPromises >= 2)
      ? 'However, previous promises were not fulfilled — verify with follow-up.'
      : 'Promise appears genuine based on language patterns.';
    summary = `Tenant has committed to payment. ${reliability}`;
  } else if (sentiment === 'positive') {
    summary = 'Tenant response is cooperative. Good candidate for continued friendly outreach.';
  } else {
    summary = 'Response is ambiguous. Recommend a follow-up message to clarify payment timeline.';
  }

  // Suggested action
  let suggestedAction;
  if (escalationRisk === 'critical' || escalationRisk === 'high') {
    suggestedAction = 'Pause automated outreach. Escalate to property manager for manual review.';
  } else if (paymentLikelihood === 'high') {
    suggestedAction = 'Set a follow-up reminder for the promised date to verify payment.';
  } else if (paymentLikelihood === 'medium') {
    suggestedAction = 'Send a gentle follow-up in 2–3 days to confirm payment timeline.';
  } else if (paymentLikelihood === 'low') {
    suggestedAction = 'Offer a structured payment plan. Consider escalating tone if no progress.';
  } else {
    suggestedAction = 'Pause automated messages. Review account for potential further action.';
  }

  return { sentiment, confidence, paymentLikelihood, escalationRisk, summary, signals, suggestedAction };
}

/**
 * Build a Map<tenantId, SentimentResult> from an array of outreach log entries.
 * Only uses entries that have a responseText. The most recent entry per tenant wins.
 *
 * @param {Array<{ tenantId, sentAt, responseText, outcome }>} outreachLogs
 * @param {Map<string, { daysLate, riskScore }>} tenantContext
 * @returns {Map<string, SentimentResult>}
 */
export function buildSentimentMap(outreachLogs, tenantContext = new Map()) {
  const results = new Map();
  const promiseCounts = new Map();

  // Count previous promises per tenant
  for (const log of outreachLogs) {
    if (log.outcome === 'promised') {
      promiseCounts.set(log.tenantId, (promiseCounts.get(log.tenantId) || 0) + 1);
    }
  }

  // Sort newest-first so we only keep the latest response per tenant
  const sorted = [...outreachLogs]
    .filter(l => l.responseText)
    .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());

  for (const log of sorted) {
    if (results.has(log.tenantId)) continue; // already have the latest for this tenant
    const ctx = tenantContext.get(log.tenantId) || {};
    const result = analyzeSentiment(log.responseText, {
      daysLate: ctx.daysLate,
      riskScore: ctx.riskScore,
      previousPromises: promiseCounts.get(log.tenantId) || 0,
    });
    results.set(log.tenantId, result);
  }

  return results;
}

/** Human-readable labels for each sentiment value */
export const SENTIMENT_CONFIG = {
  positive: { label: 'Positive', icon: '😊', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  neutral:  { label: 'Neutral',  icon: '😐', bg: 'bg-gray-100',  text: 'text-gray-600',  border: 'border-gray-200'  },
  negative: { label: 'Hardship', icon: '😟', bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  hostile:  { label: 'Hostile',  icon: '⚠️', bg: 'bg-red-100',   text: 'text-red-700',   border: 'border-red-200'   },
};

/** Human-readable labels for payment likelihood */
export const LIKELIHOOD_CONFIG = {
  high:     { label: 'High',     color: 'text-green-600' },
  medium:   { label: 'Medium',   color: 'text-amber-600' },
  low:      { label: 'Low',      color: 'text-orange-600' },
  very_low: { label: 'Very Low', color: 'text-red-600' },
};
